import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import Layout from "../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ORDER_API, MASTER_API } from "../apiConfig";
import { FileText, Download } from 'lucide-react';
import { authHeaders } from "../utils/authHeaders";

/* ===================== STYLES ===================== */

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 30,
    fontSize: "12px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
  dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
        ? "#f1f5f9"
        : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "12px",
  }),
};

const compactFilterBox = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 14,
};

const compactFilterItemLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
  paddingLeft: 2,
};

const filterLabel = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  color: "#334155",
};

const pageTitle = { fontSize: 20, fontWeight: 700, color: "#1e293b", margin: 0 };
const filterCard = {
  background: "#ffffff",
  borderRadius: 8,
  padding: 15,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 18,
};

const tableHeader = {
  padding: "10px 8px",
  borderBottom: "1px solid #ccc",
  borderRight: "1px solid #cbd5e1",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 600,
  color: "#555",
  whiteSpace: "nowrap",
};

const tableCell = {
  padding: "10px 8px",
  borderBottom: "1px solid #adb5bd",
  borderRight: "1px solid #e2e8f0",
  fontSize: "13px",
  textAlign: "center",
  color: "#444",
  wordBreak: "break-word",
  whiteSpace: "normal",
};

const linkCell = {
  color: "#1976d2",
  cursor: "pointer",
  fontWeight: 600,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
};

/* ===================== FILTER / TABLE CONFIG ===================== */

// Top filter bar fields, each mapped to its dropdown-data endpoint.
const FILTER_FIELDS = [
  { label: "Product Type", key: "productType", dataKey: "productTypes", endpoint: "product-types" },
  { label: "Category", key: "category", dataKey: "categories", endpoint: "dynamic/category" },
  { label: "Cable Type", key: "cableType", dataKey: "cableTypes", endpoint: "cable-type" },
  { label: "Code", key: "code", dataKey: "codes", endpoint: "dynamic/code" },
  { label: "Color", key: "color", dataKey: "colours", endpoint: "colour" },
  { label: "Cores", key: "noOfCores", dataKey: "cores", endpoint: "core" },
  { label: "Cross Section", key: "nominalCrossSection", dataKey: "nominalCrossSections", endpoint: "dynamic/nominal-cross-section" },
  { label: "Std. Ref", key: "referenceOfStandard", dataKey: "referenceOfStandards", endpoint: "dynamic/reference-of-standard" },
  { label: "Voltage", key: "voltage", dataKey: "voltages", endpoint: "dynamic/voltage" },
  { label: "Construction", key: "construction", dataKey: "constructions", endpoint: "construction" },
  { label: "Sub Category", key: "subCategory", dataKey: "subCategories", endpoint: "sub-category" },
  { label: "Quality Category", key: "qualityCategory", dataKey: "qualityCategories", endpoint: "quality-category" },
];

// productType values are free-text-ish (see isSfgOrRmProductType below) — this treats
// only an exact "Raw Material" match as Raw Material; everything else (Finished Goods,
// Semi Finished, Packing, ...) gets the cable-spec filter set.
const isRawMaterialType = (productType) =>
  (productType || "").trim().toLowerCase() === "raw material";

// Raw Material products are described by Category/Sub Category/Quality Category; every
// other product type is described by the cable-spec fields (Cable Type, Code, Color,
// Cores, Cross Section, Std. Ref, Voltage, Construction). Category is shared by both sets.
// Mirrors Product Master's Product Type-driven filter switch.
const CABLE_SPEC_FILTER_KEYS = [
  "category",
  "cableType",
  "code",
  "color",
  "noOfCores",
  "nominalCrossSection",
  "referenceOfStandard",
  "voltage",
  "construction",
];
const RAW_MATERIAL_FILTER_KEYS = ["category", "subCategory", "qualityCategory"];

// One column-pair (Element Description + Qty) is rendered per category below.
const PEG_CATEGORIES = [
  "Reorder Point",
  "Sales Order",
  "Outbound Delivery",
  "Stock",
  "Planned Order",
  "Production Order",
  "Purchase Request",
  "Purchase Order",
];

// Categories that carry a 3rd column between Element Description and Qty:
//   Sales Order -> Item No, Outbound Delivery -> Batch Number.
const EXTRA_COL_CATEGORIES = new Set(["Sales Order", "Outbound Delivery"]);

// Leading columns shown once per row, before the category columns.
const LEADING_COLUMNS = ["Product Code", "Product Description", "UOM"];

// Category filter row shown in the top filter card (mirrors the per-column
// "Element Description" dropdowns in the table header, minus Reorder Point).
const TOP_CATEGORY_FILTERS = [
  "Sales Order",
  "Outbound Delivery",
  "Planned Order",
  "Purchase Request",
  "Production Order",
  "Purchase Order",
  "Stock",
];

// Pixel widths/offsets for the frozen leading columns (must match the <colgroup> below).
const LEADING_COLUMN_WIDTHS = [110, 220, 60];
// Total <td> count of a table row (used by the full-width "loading / no data" rows).
const TOTAL_COLUMNS =
  LEADING_COLUMNS.length + PEG_CATEGORIES.length * 2 + EXTRA_COL_CATEGORIES.size;

const LEADING_COLUMN_LEFTS = LEADING_COLUMN_WIDTHS.reduce((acc, w, i) => {
  acc.push(i === 0 ? 0 : acc[i - 1] + LEADING_COLUMN_WIDTHS[i - 1]);
  return acc;
}, []);

// Chain/group key: scoped by product so two products never merge on a shared generic label (e.g. "Stock").
const makeChainKey = (row) => `${row.product_code || ""}::${row.child_element_description}`;

// productType values in the DB are free-text-ish: "Semi Finished", "Semi Finished " (trailing
// space), "Raw Material", "FINISHED GOODS", "PACKING" — normalize before comparing.
const isSfgOrRmProductType = (productType) => {
  const normalized = (productType || "").trim().toLowerCase();
  return normalized === "semi finished" || normalized === "raw material";
};

// A PL/PR can be pegged against more than one Sales Order; unpack the "SO::ID::QTY::ITEM || SO::..." string into rows.
// The backend already excludes ROP roots from this field (see linked_reorder_points_raw below) --
// every entry here is a genuine Sales Order and belongs in the Sales Order column only.
const parseLinkedSalesOrders = (raw) => {
  if (!raw) return [];
  return raw
    .split("||")
    .map((entry) => {
      const [number, id, qty, itemNo] = entry.split("::");
      if (!number) return null;
      return {
        number,
        id: id || null,
        qty: qty !== "" && qty != null ? Number(qty) : null,
        itemNo: itemNo !== "" && itemNo != null ? Number(itemNo) : null,
      };
    })
    .filter(Boolean);
};

// Outbound deliveries attached server-side (outbound_deliveries_raw): "OD_ID::BATCH::QTY || ...".
// od_id -> Element Description, batch_number -> Batch Number, delivery_qty -> Qty.
const parseOutboundDeliveries = (raw) => {
  if (!raw) return [];
  return raw
    .split("||")
    .map((entry) => {
      const [odId, batchNumber, qty] = entry.split("::");
      if (!odId) return null;
      return {
        odId,
        batchNumber: batchNumber || "",
        qty: qty !== "" && qty != null ? Number(qty) : 0,
      };
    })
    .filter(Boolean);
};

// Same idea, but for ROP roots resolved via planning_parent (linked_reorder_points_raw). Kept
// separate from parseLinkedSalesOrders so a ROP root can never be pushed into the Sales Order
// column -- it always renders under Reorder Point instead.
const parseLinkedRop = (raw) => {
  if (!raw) return [];
  return raw
    .split("||")
    .map((entry) => {
      const [number, id, qty] = entry.split("::");
      if (!number) return null;
      return { number, id: id || null, qty: qty !== "" && qty != null ? Number(qty) : null };
    })
    .filter(Boolean);
};

// A single PL/PR (or PO/Production Order) row can be pegged against MORE THAN ONE top-level demand
// (linked_sales_orders_raw can carry 2-3 SO entries, and/or linked_reorder_points_raw can carry a
// ROP entry) — e.g. a consolidated receipt that fulfills one Sales Order AND a separate ROP demand
// at once. Previously every linked SO was stacked into the SAME table row's Sales Order cell, and
// any linked ROP was stacked into that same row's Reorder Point cell too — so one row could show
// several SO links, or an SO and a ROP, all merged together. The requirement is one row per unique
// demand (each linked SO gets its own row; a linked ROP gets its own row too, never sharing a row
// with a linked SO), so split such a row into one synthetic row per linked demand entry — each
// carrying just that ONE entry (SO or ROP), with the other field cleared — before cells are built.
// Rows already anchored on a real top-level demand of their own (FG's own parent_category ===
// "Sales Order" or "Reorder Point") only ever have that one demand to begin with, so they pass
// through unchanged.
const explodeByLinkedSalesOrder = (row) => {
  if (row.parent_category === "Sales Order" || row.parent_category === "Reorder Point") return [row];

  const linkedSOs = parseLinkedSalesOrders(row.linked_sales_orders_raw);
  const linkedRops = parseLinkedRop(row.linked_reorder_points_raw);
  const totalLinked = linkedSOs.length + linkedRops.length;
  if (totalLinked <= 1) return [row];

  const soRows = linkedSOs.map((so, i) => ({
    ...row,
    pegging_id: `${row.pegging_id}-so${i}`,
    linked_sales_orders_raw: `${so.number}::${so.id ?? ""}::${so.qty ?? ""}::${so.itemNo ?? ""}`,
    linked_reorder_points_raw: "",
  }));

  const ropRows = linkedRops.map((rop, i) => ({
    ...row,
    pegging_id: `${row.pegging_id}-rop${i}`,
    linked_sales_orders_raw: "",
    linked_reorder_points_raw: `${rop.number}::${rop.id ?? ""}::${rop.qty ?? ""}`,
  }));

  return [...soRows, ...ropRows];
};

// After splitting, the same PR/SO pairing can still surface twice if two different pegging chain
// paths resolve to identical, fully-duplicate row content (every category/description/qty the same).
// That's noise, not a distinct requirement, so collapse rows down to unique combinations.
const rowCellSignature = (cells) =>
  JSON.stringify(
    Object.keys(cells)
      .sort()
      .map((cat) => [
        cat,
        cells[cat]
          .map((c) => `${c.description}:${c.qty}:${c.itemNo ?? ""}`)
          .sort(),
      ])
  );

// Rows whose ONLY populated column is Stock (no Sales Order / ROP / Planned Order /
// Purchase Request / Production Order / Purchase Order against them) are collapsed into
// ONE row per product. All batches stay inside that row's Stock cell and are shown through
// a "+N" button that opens a modal listing every batch.
// Rows that have any requirement/other supply linked are NOT touched.
const groupStockOnlyRows = (builtRows) => {
  const isStockOnly = ({ cells }) => {
    const filled = Object.keys(cells).filter((c) => cells[c] && cells[c].length);
    return filled.length === 1 && filled[0] === "Stock";
  };

  const groups = new Map(); // product_code -> { row, cells }
  const result = [];

  builtRows.forEach((item) => {
    if (!isStockOnly(item)) {
      result.push(item);
      return;
    }
    const key = item.row.product_code || "";
    const existing = groups.get(key);
    if (!existing) {
      const grouped = {
        row: {
          ...item.row,
          pegging_id: `stock-group-${key}`,
          __stockGroup: true,
        },
        cells: { Stock: [...item.cells.Stock] },
      };
      groups.set(key, grouped);
      result.push(grouped); // keeps the position of the first stock-only row
    } else {
      existing.cells.Stock.push(...item.cells.Stock);
    }
  });

  return result;
};

const USER_MANUAL_SCOPE = {
    moduleName: "Planning",
    process: "Planning",
    subProcess: "Planning Report"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

/* ===================== FILTER STATE PERSISTENCE ===================== */

// Filters are kept in sessionStorage so they survive navigating to a linked page
// (Sales Order, Planned Order, ...) and coming back, which unmounts this page.
const PEGGING_STATE_KEY = "pegging:filterState:v1";

const loadPeggingState = () => {
  try {
    const raw = sessionStorage.getItem(PEGGING_STATE_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
};

const savePeggingState = (state) => {
  try {
    sessionStorage.setItem(PEGGING_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    /* storage unavailable or full: ignore, page just won't restore */
  }
};

function Pegging() {
  const location = useLocation();
  const navigate = useNavigate();

  // Read once, on first render, so the page comes back exactly as it was left.
  const savedStateRef = useRef(null);
  if (savedStateRef.current === null) savedStateRef.current = loadPeggingState();
  const saved = savedStateRef.current;

  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);

  // Modal listing every stock batch of a collapsed "stock only" row.
  const [stockModal, setStockModal] = useState(null); // { productCode, batches }

  /* ---------- state ---------- */
  const [peggingData, setPeggingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dropdownData, setDropdownData] = useState({
    productTypes: [],
    categories: [],
    cableTypes: [],
    codes: [],
    colours: [],
    cores: [],
    nominalCrossSections: [],
    referenceOfStandards: [],
    voltages: [],
    constructions: [],
    subCategories: [],
    qualityCategories: [],
  });

  const [filterValues, setFilterValues] = useState(saved.filterValues || {});
  const [categoryElementFilters, setCategoryElementFilters] = useState(
    saved.categoryElementFilters || {}
  );
  // Item No filter, cascaded off the selected Sales Order (only meaningful once a
  // Sales Order is picked — an SO can have several line items).
  const [soItemNoFilter, setSoItemNoFilter] = useState(saved.soItemNoFilter || "");

  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(saved.selectedProduct || null);
  const [productDescription, setProductDescription] = useState(saved.productDescription || "");
  const [productUom, setProductUom] = useState("");

  const [headerOffsets, setHeaderOffsets] = useState({ row2: 0, row3: 0 });
  // Filters accordion: expanded by default, user can collapse to free up screen space.
  const [filtersOpen, setFiltersOpen] = useState(saved.filtersOpen !== undefined ? saved.filtersOpen : true);
  const headerRow1Ref = useRef(null);
  const headerRow2Ref = useRef(null);
  const headerRow3Ref = useRef(null);

  const returnParams = { returnTo: location.pathname };

  // Save every filter whenever it changes.
  useEffect(() => {
    savePeggingState({
      filterValues,
      categoryElementFilters,
      soItemNoFilter,
      selectedProduct,
      productDescription,
      filtersOpen,
    });
  }, [
    filterValues,
    categoryElementFilters,
    soItemNoFilter,
    selectedProduct,
    productDescription,
    filtersOpen,
  ]);

  /* =====================================================================
     PEGGING LOGIC — turns raw pegging rows into the rows/cells the table
     renders (chain building, cell grouping, row rendering, filters).
     ===================================================================== */

  // Expands each row's converted_* columns into downstream chain rows (PL->PRD->Stock, PR->PO->Stock).
  const buildChainMap = (peggingData) => {
    const map = {};
    const seen = new Set();

    peggingData.forEach((row) => {
      const descKey = row.child_element_description;
      if (!descKey) return;
      const key = makeChainKey(row);
      if (seen.has(key)) return;
      seen.add(key);

      const chain = [];

      if (row.child_category === "Planned Order" && row.converted_production_order_number) {
        chain.push({
          id: `${key}-prd`,
          product_code: row.product_code,
          product_description: row.product_description,
          child_req_date: row.converted_production_order_date,
          child_avail_date: row.converted_production_order_date,
          child_category: "Production Order",
          child_element_description: row.converted_production_order_number,
          child_qty: row.converted_production_order_qty,
          child_uom: row.child_uom,
          parent_req_date: row.child_req_date,
          parent_avail_date: row.child_avail_date,
          parent_category: "Planned Order",
          parent_element_description: descKey,
          parent_qty: row.converted_production_order_qty,
          parent_uom: row.child_uom,
        });

        const produced = Number(row.converted_production_order_produced_qty || 0);
        if (produced > 0) {
          chain.push({
            id: `${key}-prd-stock`,
            product_code: row.product_code,
            product_description: row.product_description,
            child_req_date: row.converted_production_order_date,
            child_avail_date: row.converted_production_order_date,
            child_category: "Stock",
            child_element_description: "Stock",
            child_qty: produced,
            child_uom: row.child_uom,
            parent_req_date: row.converted_production_order_date,
            parent_avail_date: row.converted_production_order_date,
            parent_category: "Production Order",
            parent_element_description: row.converted_production_order_number,
            parent_qty: produced,
            parent_uom: row.child_uom,
          });
        }
      }

      if (row.child_category === "Purchase Request" && row.converted_po_number) {
        chain.push({
          id: `${key}-po`,
          product_code: row.product_code,
          product_description: row.product_description,
          child_req_date: row.converted_po_date,
          child_avail_date: row.converted_po_date,
          child_category: "Purchase Order",
          child_element_description: row.converted_po_number,
          child_qty: row.converted_po_qty,
          child_uom: row.child_uom,
          parent_req_date: row.child_req_date,
          parent_avail_date: row.child_avail_date,
          parent_category: "Purchase Request",
          parent_element_description: descKey,
          parent_qty: row.converted_po_qty,
          parent_uom: row.child_uom,
        });

        // One chain row per GRN batch actually received against this PO.
        const batchPairs = (row.converted_po_batches || "")
          .split("||")
          .filter(Boolean)
          .map((pair) => {
            const [batchNumber, receivedQty, stockQty] = pair.split("::");
            return {
              batchNumber,
              receivedQty: Number(receivedQty || 0),
              stockQty: Number(stockQty || 0),
            };
          });

        if (batchPairs.length > 0) {
          batchPairs.forEach((b, idx) => {
            chain.push({
              id: `${key}-po-stock-${idx}`,
              product_code: row.product_code,
              product_description: row.product_description,
              child_req_date: row.converted_po_date,
              child_avail_date: row.converted_po_date,
              child_category: "Stock",
              child_element_description: b.batchNumber,
              child_qty: b.receivedQty,
              child_uom: row.child_uom,
              parent_req_date: row.converted_po_date,
              parent_avail_date: row.converted_po_date,
              parent_category: "Purchase Order",
              parent_element_description: row.converted_po_number,
              parent_qty: b.receivedQty,
              parent_uom: row.child_uom,
            });
          });
        } else {
          // No GRN batches yet — fall back to total on-hand stock so the chain doesn't break.
          const stockQty = Number(row.current_stock_qty || 0);
          if (stockQty > 0) {
            chain.push({
              id: `${key}-po-stock`,
              product_code: row.product_code,
              product_description: row.product_description,
              child_req_date: row.converted_po_date,
              child_avail_date: row.converted_po_date,
              child_category: "Stock",
              child_element_description: "Stock",
              child_qty: stockQty,
              child_uom: row.child_uom,
              parent_req_date: row.converted_po_date,
              parent_avail_date: row.converted_po_date,
              parent_category: "Purchase Order",
              parent_element_description: row.converted_po_number,
              parent_qty: stockQty,
              parent_uom: row.child_uom,
            });
          }
        }
      }

      if (chain.length) map[key] = chain;
    });

    return map;
  };

  const chainRowsByChildKey = useMemo(() => buildChainMap(peggingData), [peggingData]);

  // Option lists for the Sales Order/Item No/Planned Order/Purchase Request/Production
  // Order/Purchase Order/Stock dropdowns, loaded from GET /pegging/filter-options.
  // Deliberately NOT derived from `peggingData` — the table only loads once a filter or
  // product is picked (see fetchPegging below), but these dropdowns should show real,
  // cascading values from the start so the user has something to pick from in the first
  // place. The backend computes cascading server-side against the full matching dataset:
  // picking a value in one category narrows every other category to only what actually
  // co-occurs with it.
  const [categoryOptions, setCategoryOptions] = useState(() => {
    const empty = {};
    PEG_CATEGORIES.forEach((cat) => {
      empty[cat] = [];
    });
    return empty;
  });
  const [salesOrderItemNoOptions, setSalesOrderItemNoOptions] = useState([]);
  // fetchCategoryFilterOptions itself is defined further down, in the DATA FETCHING
  // section, once buildQueryParams exists — see the useEffect wired up there.

  // Pushes a value into a category's cell (a cell can hold several stacked values, e.g. multiple Stock batches).
  const pushCell = (cellsByCategory, category, value) => {
    if (!cellsByCategory[category]) cellsByCategory[category] = [];
    cellsByCategory[category].push(value);
  };

  // Builds every cell for one pegging path (base row + its downstream chain) as a single table row.
  const buildRowCells = (row, shownChainKeys, chainMap = chainRowsByChildKey) => {
    const cellsByCategory = {};

    // Look up the SUMMED total for this exact element (across every split of it anywhere in the
    // pegging data for this product), falling back to the row's own qty if it's a one-off. The
    // element number/description itself is still allowed to repeat across rows — only the qty
    // shown against it is the total, not a partial split.
    // "Requirement" (the raw parent_category/parent_qty pegging chain data) is only meaningful
    // for FG — for SFG/RM it's just an internal upstream link (e.g. "Planned Order"), not a real
    // requirement, so it's not shown for those product types. The true originating Sales Order for
    // SFG/RM is still resolved and rendered separately below via linked_sales_orders_raw.
    // Reorder Point is the exception: it's a legitimate requirement for SFG/RM components too
    // (not just FG), so it's always shown regardless of product type.
    const isSfgOrRm = isSfgOrRmProductType(row.product_type);
    const isRop = row.parent_category === "Reorder Point";

    if (row.parent_category && (isRop || !isSfgOrRm)) {
      const isSo = row.parent_category === "Sales Order";
      const description = isRop ? "ROP" : row.parent_element_description;
      // parent_qty_total is computed server-side (SUM() OVER PARTITION BY product/category/element)
      // so a requirement split across several rows (e.g. the same PR/SO consumed in pieces) shows
      // its full total here, not just this one row's partial slice. Falls back to the row's own
      // parent_qty for any older API response that doesn't include the total column yet.
      const qty = isRop
        ? row.product_reorder_point ?? row.parent_qty
        : isSo
          ? row.sales_order_qty ?? row.parent_qty_total ?? row.parent_qty
          : row.parent_qty_total ?? row.parent_qty;
      pushCell(cellsByCategory, row.parent_category, {
        description,
        qty,
        itemNo: isSo ? row.sales_order_item_no ?? null : undefined,
        sign: "",
        color: "#c0392b",
        category: row.parent_category,
      });
    }

    // Also render the true originating Sales Order(s) / ROP for SFG/RM rows (resolved server-side
    // via planning_parent), but ONLY when this row has no top-level demand of its own. A row whose
    // own parent_category is ALREADY "Sales Order" or "Reorder Point" is a direct demand row and its
    // own peg (pushed above) is the whole story for that row. Backfilling the linked SO/ROP on top
    // of an already-direct demand row is what caused a consolidated receipt -- one that fulfills a
    // separate Sales Order demand row AND a separate ROP demand row -- to show BOTH SO and ROP
    // duplicated across both rows (each row showing its own demand plus the sibling row's demand).
    // A Sales Order's own quantity is already whole (it isn't split the way a PR/Planned Order is),
    // so it doesn't need a totalled column — so.qty as resolved server-side is the real full qty.
    if (!isRop && row.parent_category !== "Sales Order") {
      parseLinkedSalesOrders(row.linked_sales_orders_raw).forEach((so) => {
        pushCell(cellsByCategory, "Sales Order", {
          description: so.number,
          qty: so.qty ?? row.parent_qty,
          itemNo: so.itemNo,
          sign: "",
          color: "#c0392b",
          category: "Sales Order",
        });
      });
    }

    if (row.parent_category !== "Reorder Point" && row.parent_category !== "Sales Order") {
      parseLinkedRop(row.linked_reorder_points_raw).forEach((rop) => {
        pushCell(cellsByCategory, "Reorder Point", {
          description: "ROP",
          qty: rop.qty ?? row.product_reorder_point ?? row.parent_qty,
          sign: "",
          color: "#c0392b",
          category: "Reorder Point",
        });
      });
    }

    // Outbound Delivery column: od_id is the Element Description, the delivered batch moves out of
    // the Stock column into Batch Number (below, Stock cells for these batches are skipped), and
    // Qty is the delivered qty.
    const outboundDeliveries = parseOutboundDeliveries(row.outbound_deliveries_raw);
    const deliveredBatches = new Set(outboundDeliveries.map((od) => od.batchNumber).filter(Boolean));
    outboundDeliveries.forEach((od) => {
      pushCell(cellsByCategory, "Outbound Delivery", {
        description: od.odId,
        batchNumber: od.batchNumber,
        qty: od.qty,
        sign: "",
        color: "#2e7d32",
        category: "Outbound Delivery",
      });
    });

    const descKey = row.child_element_description;
    if (row.child_category && descKey) {
      // Dedup only applies to the extra downstream chain, never to this row's own direct peg.
      const key = makeChainKey(row);
      const chainAlreadyShownElsewhere = shownChainKeys.has(key);
      if (!chainAlreadyShownElsewhere) shownChainKeys.add(key);

      const chain = chainMap[key] || [];

      // Combine base supply element + chain so each stage can tell if it was consumed further downstream.
      const combined = [
        {
          child_category: row.child_category,
          child_element_description: descKey,
          child_qty: row.child_qty,
        },
        ...chain,
      ];

      combined.forEach((item) => {
        const consumedFurther = combined.some(
          (other) => other.parent_element_description === item.child_element_description
        );
        if (consumedFurther) return;

        const isBaseElement =
          item.child_category === row.child_category &&
          item.child_element_description === descKey;

        if (!isBaseElement && chainAlreadyShownElsewhere) return;

        // Batch already delivered via an Outbound Delivery -> it lives in that column now.
        if (item.child_category === "Stock" && deliveredBatches.has(item.child_element_description)) {
          return;
        }

        // Only this row's own direct child element (e.g. the Purchase Request itself) can be split
        // across multiple pegging rows in the DB, so only it gets the server-computed total swapped
        // in. Downstream chain items (converted PO/Production Order/Stock) come from this row's own
        // converted_* fields and aren't fragmented the same way.
        // Stock is special-cased: child_qty_total for Stock sums rec_qty (how much has
        // been consumed/allocated from this batch across pegging rows) — NOT the batch's
        // original quantity. For Stock cells, show receipt_org_qty (the batch's org_qty)
        // instead. Every other category keeps the server-computed split total.
        const qty = isBaseElement
          ? item.child_category === "Stock"
            ? row.receipt_org_qty ?? item.child_qty
            : row.child_qty_total ?? item.child_qty
          : item.child_qty;

        pushCell(cellsByCategory, item.child_category, {
          description: item.child_element_description,
          qty,
          sign: "+",
          color: "#2e7d32",
          category: item.child_category,
        });
      });
    }

    return cellsByCategory;
  };

  // Resolves the click-through navigation target for a given category/element.
  const navigateFor = (category, description) => {
    if (!description) return null;
    if (category === "Sales Order") {
      return () =>
        navigate("/cable/sales/sales-order", {
          state: { openSoId: description, ...returnParams },
        });
    }
    if (category === "Outbound Delivery") {
      return () =>
        navigate("/cable/outbound-process/outbound-delivery", {
          state: { openOdId: description, ...returnParams },
        });
    }
    if (category === "Planned Order") {
      return () =>
        navigate("/planned-order", {
          state: { openPlannedOrderNo: description, ...returnParams },
        });
    }
    if (category === "Production Order") {
      return () =>
        navigate("/cable/production-order", {
          state: { openProductionOrderNo: description, ...returnParams },
        });
    }
    if (category === "Purchase Request") {
      return () =>
        navigate("/cable/procurement/purchase-request", {
          state: { openPrNo: description, ...returnParams },
        });
    }
    if (category === "Purchase Order") {
      return () =>
        navigate("/cable/procurement/purchase-order", {
          state: { openPoNo: description, ...returnParams },
        });
    }
    return null;
  };

  // Renders a category cell's description as a clickable link, when it navigates somewhere.
  const renderElement = (category, description) => {
    if (!description) return "";
    const onClick = navigateFor(category, description);
    if (!onClick) return description;
    return (
      <span style={linkCell} onClick={onClick}>
        {description}
      </span>
    );
  };

  // Renders one <tr> for a built row (leading columns + every category's Element/Qty cells).
  const renderRow = (cellsByCategory, key, row = null) => {
    const cellBorder = "1px solid #cbd5e1";
    return (
      <tr key={key} style={{ background: "#fff" }}>
        <td
          className="peg-sticky-col"
          style={{
            ...tableCell,
            borderBottom: cellBorder,
            borderRight: "1px solid #cbd5e1",
            textAlign: "left",
            fontWeight: 600,
            left: LEADING_COLUMN_LEFTS[0],
          }}
        >
          {row?.product_code || ""}
        </td>
        <td
          className="peg-sticky-col"
          style={{
            ...tableCell,
            borderBottom: cellBorder,
            borderRight: "1px solid #cbd5e1",
            textAlign: "left",
            left: LEADING_COLUMN_LEFTS[1],
          }}
        >
          {row?.product_description || ""}
        </td>
        <td
          className="peg-sticky-col"
          style={{
            ...tableCell,
            borderBottom: cellBorder,
            borderRight: "2px solid #94a3b8",
            left: LEADING_COLUMN_LEFTS[2],
          }}
        >
          {row?.child_uom || row?.parent_uom || ""}
        </td>
        {PEG_CATEGORIES.map((cat) => {
          const values = cellsByCategory[cat];

          // Stock-only row with several batches: show the first batch + a "+N" button
          // (opens a modal listing every batch) and the summed qty.
          const isStockGroup =
            cat === "Stock" && row?.__stockGroup && values && values.length > 1;

          if (isStockGroup) {
            const total = values.reduce((s, v) => s + Number(v.qty || 0), 0);
            return (
              <React.Fragment key={cat}>
                <td style={{ ...tableCell, borderBottom: cellBorder }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                    }}
                  >
                    <span>{values[0].description}</span>
                    <button
                      type="button"
                      title={`View all ${values.length} batches`}
                      onClick={() =>
                        setStockModal({
                          productCode: row.product_code,
                          batches: values,
                        })
                      }
                      style={{
                        border: "1px solid #2e7d32",
                        background: "#fff",
                        color: "#2e7d32",
                        borderRadius: 4,
                        padding: "0 6px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        lineHeight: "18px",
                      }}
                    >
                      +{values.length - 1}
                    </button>
                  </div>
                </td>
                <td
                  style={{
                    ...tableCell,
                    borderBottom: cellBorder,
                    borderRight: "2px solid #94a3b8",
                  }}
                >
                  <div style={{ color: "#2e7d32", fontWeight: 700 }}>
                    +{total.toLocaleString()}
                  </div>
                </td>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={cat}>
              <td style={{ ...tableCell, borderBottom: cellBorder }}>
                {values && values.length
                  ? values.map((v, i) => (
                    <div key={i} style={i > 0 ? { marginTop: 4 } : undefined}>
                      {renderElement(v.category, v.description)}
                    </div>
                  ))
                  : ""}
              </td>
              {EXTRA_COL_CATEGORIES.has(cat) && (
                <td style={{ ...tableCell, borderBottom: cellBorder }}>
                  {values && values.length
                    ? values.map((v, i) => (
                      <div key={i} style={i > 0 ? { marginTop: 4 } : undefined}>
                        {cat === "Outbound Delivery" ? v.batchNumber ?? "" : v.itemNo ?? ""}
                      </div>
                    ))
                    : ""}
                </td>
              )}
              <td
                style={{
                  ...tableCell,
                  borderBottom: cellBorder,
                  borderRight: "2px solid #94a3b8",
                }}
              >
                {values && values.length
                  ? values.map((v, i) => (
                    <div
                      key={i}
                      style={{
                        color: v.color,
                        fontWeight: 700,
                        marginTop: i > 0 ? 4 : 0,
                      }}
                    >
                      {v.sign}
                      {Number(v.qty || 0).toLocaleString()}
                    </div>
                  ))
                  : ""}
              </td>
            </React.Fragment>
          );
        })}
      </tr>
    );
  };

  // Updates the Element Description filter for one category column. Changing the Sales
  // Order filter clears the cascaded Item No filter, since it belongs to the old SO.
  const handleCategoryElementFilterChange = (cat, value) => {
    setCategoryElementFilters((prev) => {
      const next = { ...prev };
      if (value) next[cat] = value;
      else delete next[cat];
      return next;
    });
    if (cat === "Sales Order") setSoItemNoFilter("");
  };

  /* ===================== EXPORT (CSV, opens in Excel) =====================
     Filters active  -> exports exactly what the table shows for those filters.
     No filter       -> fetches EVERYTHING from GET /pegging and exports it all.
     Uses the same row-building rules as the table (one line per receipt). */
  const [exporting, setExporting] = useState(false);

  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const buildExportRows = (data) => {
    const chainMap = buildChainMap(data);
    const shownChainKeys = new Set();
    const seenSignatures = new Set();
    const SUPPLY_CATS = ["Stock", "Planned Order", "Production Order", "Purchase Request", "Purchase Order"];

    let built = data
      .flatMap(explodeByLinkedSalesOrder)
      .map((row) => ({ row, cells: buildRowCells(row, shownChainKeys, chainMap) }))
      .filter(({ cells }) => Object.values(cells).some((e) => e && e.length > 0))
      .filter(({ cells }) => {
        const sig = rowCellSignature(cells);
        if (seenSignatures.has(sig)) return false;
        seenSignatures.add(sig);
        return true;
      });

    // Same requirement, different receipt number => separate lines.
    built = built.flatMap(({ row, cells }) => {
      const lineCount = Math.max(1, ...SUPPLY_CATS.map((c) => (cells[c] ? cells[c].length : 0)));
      if (lineCount <= 1) return [{ row, cells }];
      return Array.from({ length: lineCount }, (_, i) => {
        const lineCells = { ...cells };
        SUPPLY_CATS.forEach((c) => {
          const list = cells[c] || [];
          if (list.length > 1) lineCells[c] = list[i] ? [list[i]] : [];
        });
        return { row, cells: lineCells };
      });
    });

    // Per-column filters (same behaviour as the table).
    const activeCat = Object.entries(categoryElementFilters).filter(([, v]) => v);
    if (activeCat.length) {
      built = built
        .filter(({ cells }) =>
          activeCat.every(([cat, val]) => (cells[cat] || []).some((v) => v.description === val))
        )
        .map(({ row, cells }) => {
          const fc = { ...cells };
          activeCat.forEach(([cat, val]) => {
            fc[cat] = (fc[cat] || []).filter((v) => v.description === val);
          });
          return { row, cells: fc };
        });
    }
    if (soItemNoFilter) {
      built = built
        .filter(({ cells }) =>
          (cells["Sales Order"] || []).some((v) => String(v.itemNo ?? "") === soItemNoFilter)
        )
        .map(({ row, cells }) => ({
          row,
          cells: {
            ...cells,
            "Sales Order": (cells["Sales Order"] || []).filter(
              (v) => String(v.itemNo ?? "") === soItemNoFilter
            ),
          },
        }));
    }
    return built;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const hasAnyFilter =
        Object.keys(filterValues).some((k) => filterValues[k]) ||
        Object.keys(categoryElementFilters).some((k) => categoryElementFilters[k]) ||
        !!soItemNoFilter ||
        !!selectedProduct?.value;

      let data = peggingData;
      if (!hasAnyFilter) {
        // Table is empty by design when nothing is selected, so pull everything.
        const res = await axios.get(`${ORDER_API}/pegging`);
        const seen = new Set();
        data = (res.data || []).filter((r) => {
          if (seen.has(r.pegging_id)) return false;
          seen.add(r.pegging_id);
          return true;
        });
      }

      const rows = buildExportRows(data);
      if (!rows.length) {
        alert("Nothing to export for the current filters.");
        return;
      }

      const header = ["Product Code", "Product Description", "UOM"];
      PEG_CATEGORIES.forEach((cat) => {
        header.push(`${cat} - Element Description`);
        if (cat === "Sales Order") header.push("Sales Order - Item No");
        if (cat === "Outbound Delivery") header.push("Outbound Delivery - Batch Number");
        header.push(`${cat} - Qty`);
      });

      const joinVals = (vals, pick) => {
        if (!vals || !vals.length) return "";
        if (vals.length === 1) return pick(vals[0]) ?? "";
        return vals.map((v) => pick(v) ?? "").join(" | ");
      };

      const lines = [header.map(csvEscape).join(",")];
      rows.forEach(({ row, cells }) => {
        const line = [
          row.product_code || "",
          row.product_description || "",
          row.child_uom || row.parent_uom || "",
        ];
        PEG_CATEGORIES.forEach((cat) => {
          const vals = cells[cat];
          line.push(joinVals(vals, (v) => v.description));
          if (cat === "Sales Order") line.push(joinVals(vals, (v) => v.itemNo));
          if (cat === "Outbound Delivery") line.push(joinVals(vals, (v) => v.batchNumber));
          line.push(joinVals(vals, (v) => Number(v.qty || 0)));
        });
        lines.push(line.map(csvEscape).join(","));
      });

      const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pegging_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Pegging export failed:", err);
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount =
    Object.keys(filterValues).length +
    (selectedProduct ? 1 : 0) +
    Object.keys(categoryElementFilters).length +
    (soItemNoFilter ? 1 : 0);

  // Fields whose dropdown options are dynamically scoped to the selected Product Type
  // (fetched from /product-master/filter-options, same endpoint Product Master uses).
  const DEPENDENT_FILTER_KEYS = [
    "category",
    "cableType",
    "code",
    "color",
    "noOfCores",
    "nominalCrossSection",
    "referenceOfStandard",
    "voltage",
    "construction",
    "subCategory",
    "qualityCategory",
  ];

  // Updates a top filter bar field and resets the selected product. Changing Product Type
  // also clears every dependent filter, since their available options change with it.
  const handleFilterChange = (key, selected) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (selected?.value) next[key] = selected.value;
      else delete next[key];
      if (key === "productType") {
        DEPENDENT_FILTER_KEYS.forEach((k) => delete next[k]);
      }
      return next;
    });
    setSelectedProduct(null);
    setProductDescription("");
    setProductUom("");
  };

  // Resets every filter (top bar, product, per-column) back to empty.
  const clearAllFilters = () => {
    setFilterValues({});
    setSelectedProduct(null);
    setProductDescription("");
    setProductUom("");
    setCategoryElementFilters({});
    setSoItemNoFilter("");
    try { sessionStorage.removeItem(PEGGING_STATE_KEY); } catch (e) { /* ignore */ }
  };

  /* =====================================================================
     DATA FETCHING — everything that talks to the API lives below.
     ===================================================================== */

  // Turns the active top-bar filters into query params shared by every fetch call.
  const buildQueryParams = useCallback(
    (extra = {}) => {
      const params = {};
      FILTER_FIELDS.forEach((f) => {
        if (filterValues[f.key]) params[f.key] = filterValues[f.key];
      });
      return { ...params, ...extra };
    },
    [filterValues]
  );

  // Loads the Sales Order/Item No/Planned Order/Purchase Request/Production Order/Purchase
  // Order/Stock dropdown options from GET /pegging/filter-options. Unlike fetchPegging below,
  // this always runs (no "pick something first" gate) — these dropdowns exist to help the
  // user find what to filter on, so they need real values before anything else is selected.
  // Passing the currently-selected per-column values lets the backend cascade every OTHER
  // category's options down to what actually co-occurs with the current selection.
  const fetchCategoryFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${ORDER_API}/pegging/filter-options`, {
        params: buildQueryParams({
          salesOrder: categoryElementFilters["Sales Order"] || "",
          outboundDelivery: categoryElementFilters["Outbound Delivery"] || "",
          itemNo: soItemNoFilter || "",
          plannedOrder: categoryElementFilters["Planned Order"] || "",
          purchaseRequest: categoryElementFilters["Purchase Request"] || "",
          productionOrder: categoryElementFilters["Production Order"] || "",
          purchaseOrder: categoryElementFilters["Purchase Order"] || "",
          stock: categoryElementFilters["Stock"] || "",
        }),
      });
      const data = res.data || {};
      setCategoryOptions({
        "Reorder Point": data.reorderPoint || [],
        "Sales Order": data.salesOrder || [],
        "Outbound Delivery": data.outboundDelivery || [],
        "Planned Order": data.plannedOrder || [],
        "Purchase Request": data.purchaseRequest || [],
        "Production Order": data.productionOrder || [],
        "Purchase Order": data.purchaseOrder || [],
        Stock: data.stock || [],
      });
      setSalesOrderItemNoOptions(data.itemNo || []);
    } catch (err) {
      console.error("Failed to load pegging filter options:", err);
    }
  }, [buildQueryParams, categoryElementFilters, soItemNoFilter]);

  useEffect(() => {
    fetchCategoryFilterOptions();
  }, [fetchCategoryFilterOptions]);

  // Loads the Product Code dropdown options for the current filters.
  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${ORDER_API}/pegging/products`, {
        params: buildQueryParams(),
      });
      setProducts(res.data || []);
    } catch (err) {
      console.error("Failed to load pegged products:", err);
    }
  }, [buildQueryParams]);

  // Loads the pegging table rows for the current filters/selected product/per-column
  // category filters. Nothing should load by default: only fetch when at least one
  // top-bar filter, a per-column filter (Sales Order/Item No/Planned Order/Purchase
  // Request/Production Order/Purchase Order/Stock), or a product is active. Otherwise
  // clear the table instead of calling the "give me everything" endpoint.
  const fetchPegging = useCallback(async () => {
    const activeTopFilters = Object.keys(filterValues).filter((k) => filterValues[k]);
    const activeCategoryFilters = Object.keys(categoryElementFilters).filter(
      (k) => categoryElementFilters[k]
    );
    const hasAnyFilter =
      activeTopFilters.length > 0 ||
      activeCategoryFilters.length > 0 ||
      !!soItemNoFilter ||
      !!selectedProduct?.value;

    if (!hasAnyFilter) {
      setPeggingData([]);
      setProductUom("");
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${ORDER_API}/pegging`, {
        params: buildQueryParams({
          ...(selectedProduct?.value ? { productCode: selectedProduct.value } : {}),
          salesOrder: categoryElementFilters["Sales Order"] || "",
          outboundDelivery: categoryElementFilters["Outbound Delivery"] || "",
          itemNo: soItemNoFilter || "",
          plannedOrder: categoryElementFilters["Planned Order"] || "",
          purchaseRequest: categoryElementFilters["Purchase Request"] || "",
          productionOrder: categoryElementFilters["Production Order"] || "",
          purchaseOrder: categoryElementFilters["Purchase Order"] || "",
          stock: categoryElementFilters["Stock"] || "",
        }),
      });
      const rawRows = res.data || [];

      // Dedupe by pegging_id — backend join can occasionally fan out a duplicate row.
      const seenPeggingIds = new Set();
      const rows = rawRows.filter((r) => {
        if (seenPeggingIds.has(r.pegging_id)) return false;
        seenPeggingIds.add(r.pegging_id);
        return true;
      });
      setPeggingData(rows);
      setProductUom(rows[0]?.child_uom || rows[0]?.parent_uom || "");
    } catch (err) {
      console.error(err);
      setError("Failed to load pegging data.");
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, selectedProduct, filterValues, categoryElementFilters, soItemNoFilter]);

  // Loads the Product Type dropdown once on mount — it's the only top filter bar
  // field whose options never depend on another filter.
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const res = await axios.get(`${MASTER_API}/configuration/product-types`);
        setDropdownData((prev) => ({ ...prev, productTypes: res.data || [] }));
      } catch (err) {
        console.error("Failed to load product types:", err);
      }
    };
    fetchProductTypes();
  }, []);

  // Loads Category/Cable Type/Code/Color/Cores/Cross Section/Std. Ref/Voltage/Construction
  // options scoped to whatever's currently selected — same /product-master/filter-options
  // endpoint Product Master uses, so picking a Product Type here narrows these dropdowns to
  // only the values that actually occur for that product type (and each other selection
  // narrows the rest further, same cascading behavior as Product Master).
  const fetchDependentFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${MASTER_API}/product-master/filter-options`, {
        params: {
          productType: filterValues.productType || "",
          category: filterValues.category || "",
          cableType: filterValues.cableType || "",
          code: filterValues.code || "",
          color: filterValues.color || "",
          noOfCores: filterValues.noOfCores || "",
          nominalCrossSection: filterValues.nominalCrossSection || "",
          referenceOfStandard: filterValues.referenceOfStandard || "",
          voltage: filterValues.voltage || "",
          construction: filterValues.construction || "",
          subCategory: filterValues.subCategory || "",
          qualityCategory: filterValues.qualityCategory || "",
        },
      });
      const data = res.data || {};
      // The endpoint returns flat string arrays; reshape to {name} objects so the
      // existing dropdown rendering (which reads `item.name`) needs no other changes.
      const toNameObjs = (arr) => (arr || []).map((v) => ({ name: v }));
      setDropdownData((prev) => ({
        ...prev,
        categories: toNameObjs(data.category),
        cableTypes: toNameObjs(data.cableType),
        codes: toNameObjs(data.code),
        colours: toNameObjs(data.color),
        cores: toNameObjs(data.noOfCores),
        nominalCrossSections: toNameObjs(data.nominalCrossSection),
        referenceOfStandards: toNameObjs(data.referenceOfStandard),
        voltages: toNameObjs(data.voltage),
        constructions: toNameObjs(data.construction),
        subCategories: toNameObjs(data.subCategory),
        qualityCategories: toNameObjs(data.qualityCategory),
      }));
    } catch (err) {
      console.error("Failed to load dependent filter options:", err);
    }
  }, [
    filterValues.productType,
    filterValues.category,
    filterValues.cableType,
    filterValues.code,
    filterValues.color,
    filterValues.noOfCores,
    filterValues.nominalCrossSection,
    filterValues.referenceOfStandard,
    filterValues.voltage,
    filterValues.construction,
    filterValues.subCategory,
    filterValues.qualityCategory,
  ]);

  useEffect(() => {
    fetchDependentFilterOptions();
  }, [fetchDependentFilterOptions]);

  // Refetches products + pegging rows whenever a top filter bar field changes, and clears
  // any per-column category filter (it belonged to the old scope).
  // The reset only applies when the top filters actually CHANGED. On first mount (e.g.
  // coming back from a popup page) the restored per-column filters must be kept.
  const prevFilterValuesRef = useRef(filterValues);
  useEffect(() => {
    if (prevFilterValuesRef.current !== filterValues) {
      prevFilterValuesRef.current = filterValues;
      setCategoryElementFilters((prev) => (Object.keys(prev).length ? {} : prev));
      setSoItemNoFilter("");
    }
    fetchProducts();
    fetchPegging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  // Refetches pegging rows whenever the selected product changes, and clears any
  // per-column category filter (it belonged to the old product's data).
  const prevSelectedProductRef = useRef(selectedProduct);
  useEffect(() => {
    if (prevSelectedProductRef.current !== selectedProduct) {
      prevSelectedProductRef.current = selectedProduct;
      setCategoryElementFilters((prev) => (Object.keys(prev).length ? {} : prev));
      setSoItemNoFilter("");
    }
    fetchPegging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  // Refetches pegging rows whenever a per-column category filter (Sales Order/Item No/
  // Planned Order/Purchase Request/Production Order/Purchase Order/Stock) changes — this
  // is what makes "pick a Sales Order and the table populates" actually work.
  useEffect(() => {
    fetchPegging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryElementFilters, soItemNoFilter]);

  // react-select options built from the loaded products list.
  const productOptions = products.map((item) => ({
    value: item.productCode,
    label: item.productCode,
    description: item.productDescription,
  }));

  // Measures the 3 sticky header rows' rendered heights so rows below can be offset correctly.
  useLayoutEffect(() => {
    const measure = () => {
      const h1 = headerRow1Ref.current?.getBoundingClientRect().height || 0;
      const h2 = headerRow2Ref.current?.getBoundingClientRect().height || 0;
      setHeaderOffsets((prev) => {
        const next = { row2: h1, row3: h1 + h2 };
        if (prev.row2 === next.row2 && prev.row3 === next.row3) return prev;
        return next;
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });

  /* =====================================================================
     RENDER
     ===================================================================== */

  return (
    <Layout defaultSidebarOpen={false}>
      <div style={{ padding: 20 }}>
        <style>{`
          .peg-scroll-container {
            overflow: auto;
            max-height: 70vh;
            border: 1px solid #ddd;
            border-radius: 6px;
          }
          .peg-scroll-container::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .peg-scroll-container::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .peg-scroll-container::-webkit-scrollbar-thumb {
            background: #b0bec5;
            border-radius: 6px;
          }
          .peg-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #90a4ae;
          }
          .peg-table thead th {
            position: sticky;
            top: 0;
            z-index: 3;
          }
          .peg-sticky-col {
            position: sticky;
            z-index: 2;
            background: #fff;
          }
          .peg-sticky-header {
            position: sticky;
            z-index: 4 !important;
            background: #5c6472;
          }
        `}</style>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 style={pageTitle}>Planning Report</h2>
          <button
            onClick={() => {
              setManualExists(null);
              setManualFile(null);
              setShowManual(true);
              fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                .then((res) => res.json())
                .then((data) => {
                  const norm = (v) => String(v || "").trim().toLowerCase();
                  const match = (data?.data || []).find((m) =>
                    norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                    norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                    norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                  );
                  if (match && match.drive_link) {
                    setManualFile(match);
                    setManualExists(true);
                  } else {
                    setManualExists(false);
                  }
                })
                .catch(() => setManualExists(false));
            }}
            title="View User Manual"
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "#3b82f6",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
          >
            <FileText size={18} />
            User Manual
          </button>

          {/* --- User Manual Popup Modal --- */}
          {showManual && (
            <div
              onClick={() => setShowManual(false)}
              style={{
                position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                alignItems: "center", justifyContent: "center", zIndex: 1100,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  width: "90vw",
                  height: "97vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                }}
              >
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                }}>
                  <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                    User Manual
                  </h4>
                  <button
                    onClick={() => setShowManual(false)}
                    style={{
                      border: "none", background: "#f1f5f9", borderRadius: "8px",
                      width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                      color: "#475569", lineHeight: 1,
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>

                {manualExists === null && (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                    Checking for user manual...
                  </div>
                )}

                {manualExists === false && (
                  <div style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                  }}>
                    <FileText size={40} color="#cbd5e1" />
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                      User Manual Not Available
                    </div>
                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                      The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                    </div>
                  </div>
                )}

                {manualExists === true && manualFile && (() => {
                  const MANUAL_SCALE = 1.25;
                  return (
                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                      <iframe
                        src={manualFile.drive_link.replace("/view", "/preview")}
                        title={manualFile.file_name || "User Manual"}
                        allow="autoplay"
                        style={{
                          border: "none",
                          width: `${100 / MANUAL_SCALE}%`,
                          height: `${100 / MANUAL_SCALE}%`,
                          transform: `scale(${MANUAL_SCALE})`,
                          transformOrigin: "top left",
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        <div style={filterCard}>
          {/* Accordion header — click to show / hide the filters */}
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="peg-filters-body"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0 10px 0",
              background: "transparent",
              border: "none",
              borderBottom: filtersOpen ? "1px solid #eef0f3" : "none",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {activeFilterCount} active
                </span>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                role="button"
                title="Export to CSV: uses the selected filters, or everything if none"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!exporting) handleExport();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  background: "#fff",
                  color: "#2e7d32",
                  border: "1px solid #2e7d32",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: exporting ? "not-allowed" : "pointer",
                  opacity: exporting ? 0.6 : 1,
                }}
              >
                <Download size={13} />
                {exporting ? "Exporting..." : "Export"}
              </span>
              {activeFilterCount > 0 && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  style={{
                    padding: "4px 10px",
                    background: "#fff",
                    color: "#c0392b",
                    border: "1px solid #c0392b",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Clear Filters
                </span>
              )}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 12px",
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    color: "#555",
                    transition: "transform 0.25s ease",
                    transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▼
                </span>
              </span>
            </span>
          </button>

          <div
            id="peg-filters-body"
            style={{
              display: "grid",
              gridTemplateRows: filtersOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 0.3s ease",
            }}
          >
            <div style={{ overflow: filtersOpen ? "visible" : "hidden", minHeight: 0 }}>
              <div style={{ paddingTop: 14 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "end",
                    gap: "14px",
                    marginBottom: "14px",
                  }}
                >
                  <div style={{ minWidth: 200, maxWidth: 260, flex: "0 0 auto" }}>
                    {FILTER_FIELDS.filter((f) => f.key === "productType").map((f) => (
                      <div key={f.key}>
                        <label style={filterLabel}>{f.label}</label>
                        <Select
                          options={(dropdownData[f.dataKey] || []).map((item) => ({
                            value: item.name,
                            label: item.name,
                          }))}
                          value={
                            filterValues[f.key]
                              ? { value: filterValues[f.key], label: filterValues[f.key] }
                              : null
                          }
                          onChange={(selected) => handleFilterChange(f.key, selected)}
                          placeholder="Select"
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div style={compactFilterBox}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    {(isRawMaterialType(filterValues.productType) ? RAW_MATERIAL_FILTER_KEYS : CABLE_SPEC_FILTER_KEYS)
                      .map((key) => FILTER_FIELDS.find((f) => f.key === key))
                      .map((f) => (
                        <div
                          key={f.key}
                          style={{
                            minWidth: 120,
                            flex: "1 1 110px",
                            maxWidth: 180,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <span style={compactFilterItemLabel}>{f.label}</span>
                          <Select
                            options={(dropdownData[f.dataKey] || []).map((item) => ({
                              value: item.name,
                              label: item.name,
                            }))}
                            value={
                              filterValues[f.key]
                                ? { value: filterValues[f.key], label: filterValues[f.key] }
                                : null
                            }
                            onChange={(selected) => handleFilterChange(f.key, selected)}
                            placeholder="Select"
                            isClearable
                            styles={compactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      ))}
                  </div>
                </div>

                <div style={compactFilterBox}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    {TOP_CATEGORY_FILTERS.map((cat) => (
                      <React.Fragment key={cat}>
                        <div
                          style={{
                            minWidth: 140,
                            flex: "1 1 140px",
                            maxWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <span style={compactFilterItemLabel}>{cat}</span>
                          <Select
                            options={(categoryOptions[cat] || []).map((opt) => ({
                              value: opt,
                              label: opt,
                            }))}
                            value={
                              categoryElementFilters[cat]
                                ? {
                                  value: categoryElementFilters[cat],
                                  label: categoryElementFilters[cat],
                                }
                                : null
                            }
                            onChange={(selected) =>
                              handleCategoryElementFilterChange(cat, selected?.value || "")
                            }
                            placeholder="All"
                            isClearable
                            styles={compactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>

                        {cat === "Sales Order" && (
                          <div
                            style={{
                              minWidth: 110,
                              flex: "1 1 110px",
                              maxWidth: 150,
                              display: "flex",
                              flexDirection: "column",
                              gap: 3,
                            }}
                          >
                            <span style={compactFilterItemLabel}>Item No</span>
                            <Select
                              options={salesOrderItemNoOptions.map((opt) => ({
                                value: opt,
                                label: opt,
                              }))}
                              value={
                                soItemNoFilter
                                  ? { value: soItemNoFilter, label: soItemNoFilter }
                                  : null
                              }
                              onChange={(selected) => setSoItemNoFilter(selected?.value || "")}
                              placeholder={
                                categoryElementFilters["Sales Order"] ? "All" : "Select SO first"
                              }
                              isClearable
                              isDisabled={!categoryElementFilters["Sales Order"]}
                              styles={compactSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr minmax(90px, 140px)",
                    gap: "14px",
                  }}
                >
                  <div>
                    <label style={filterLabel}>Product Code</label>
                    <Select
                      options={productOptions}
                      value={selectedProduct}
                      onChange={(selected) => {
                        setSelectedProduct(selected);
                        setProductDescription(selected?.description || "");
                        setProductUom("");
                      }}
                      placeholder="Search Product Code"
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label style={filterLabel}>Product Description</label>
                    <input
                      type="text"
                      value={productDescription}
                      readOnly
                      placeholder="Product Description"
                      style={{
                        width: "100%",
                        height: "32px",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "0 10px",
                        fontSize: "13px",
                        background: "#f9f9f9",
                      }}
                    />
                  </div>

                  <div>
                    <label style={filterLabel}>UOM</label>
                    <input
                      type="text"
                      value={productUom}
                      readOnly
                      placeholder="UOM"
                      style={{
                        width: "100%",
                        height: "32px",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "0 10px",
                        fontSize: "13px",
                        background: "#f9f9f9",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div className="peg-scroll-container" style={{ marginTop: 12 }}>
          <table
            className="peg-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
              minWidth: 1500,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {LEADING_COLUMN_WIDTHS.map((w, i) => (
                <col key={`leading-col-${i}`} style={{ width: w }} />
              ))}
              {PEG_CATEGORIES.map((cat) => (
                <React.Fragment key={`${cat}-col`}>
                  <col style={{ width: 150 }} />
                  {cat === "Sales Order" && <col style={{ width: 70 }} />}
                  {cat === "Outbound Delivery" && <col style={{ width: 120 }} />}
                  <col style={{ width: 90 }} />
                </React.Fragment>
              ))}
            </colgroup>
            <thead>
              <tr ref={headerRow1Ref} style={{ background: "#5c6472" }}>
                {LEADING_COLUMNS.map((col, i) => (
                  <th
                    key={col}
                    rowSpan="3"
                    className="peg-sticky-header"
                    style={{
                      ...tableHeader,
                      color: "#fff",
                      background: "#5c6472",
                      borderRight: "2px solid #454c58",
                      left: LEADING_COLUMN_LEFTS[i],
                    }}
                  >
                    {col}
                  </th>
                ))}
                {PEG_CATEGORIES.map((cat) => (
                  <th
                    key={cat}
                    colSpan={EXTRA_COL_CATEGORIES.has(cat) ? "3" : "2"}
                    style={{
                      ...tableHeader,
                      color: "#fff",
                      background: "#5c6472",
                      borderRight: "2px solid #454c58",
                    }}
                  >
                    {cat}
                  </th>
                ))}
              </tr>
              <tr ref={headerRow2Ref} style={{ background: "#d5d8dd" }}>
                {PEG_CATEGORIES.map((cat) => (
                  <React.Fragment key={cat}>
                    <th
                      style={{
                        ...tableHeader,
                        background: "#d5d8dd",
                        color: "#2c313a",
                        top: headerOffsets.row2,
                      }}
                    >
                      Element Description
                    </th>
                    {EXTRA_COL_CATEGORIES.has(cat) && (
                      <th
                        style={{
                          ...tableHeader,
                          background: "#d5d8dd",
                          color: "#2c313a",
                          top: headerOffsets.row2,
                        }}
                      >
                        {cat === "Outbound Delivery" ? "Batch Number" : "Item No"}
                      </th>
                    )}
                    <th
                      style={{
                        ...tableHeader,
                        background: "#d5d8dd",
                        color: "#2c313a",
                        borderRight: "2px solid #94a3b8",
                        top: headerOffsets.row2,
                      }}
                    >
                      Qty
                    </th>
                  </React.Fragment>
                ))}
              </tr>
              <tr ref={headerRow3Ref} style={{ background: "#fff" }}>
                {PEG_CATEGORIES.map((cat) => (
                  <th
                    key={`${cat}-filter`}
                    colSpan={EXTRA_COL_CATEGORIES.has(cat) ? "3" : "2"}
                    style={{
                      position: "sticky",
                      top: headerOffsets.row3,
                      zIndex: 3,
                      padding: "6px 8px",
                      background: "#fff",
                      borderRight: "2px solid #94a3b8",
                      borderBottom: "1px solid #cbd5e1",
                    }}
                  >
                    <Select
                      options={categoryOptions[cat].map((opt) => ({
                        value: opt,
                        label: opt,
                      }))}
                      value={
                        categoryElementFilters[cat]
                          ? {
                            value: categoryElementFilters[cat],
                            label: categoryElementFilters[cat],
                          }
                          : null
                      }
                      onChange={(selected) =>
                        handleCategoryElementFilterChange(cat, selected?.value || "")
                      }
                      placeholder="All"
                      isClearable
                      styles={compactSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={TOTAL_COLUMNS} style={tableCell}>
                    Loading pegging data...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={TOTAL_COLUMNS}
                    style={{ ...tableCell, color: "#c0392b" }}
                  >
                    {error}
                  </td>
                </tr>
              ) : peggingData.length === 0 ? (
                <tr>
                  <td colSpan={TOTAL_COLUMNS} style={tableCell}>
                    No pegging data found{selectedProduct ? ` for ${selectedProduct.value}` : " for these filters"}.
                  </td>
                </tr>
              ) : (
                (() => {
                  const shownChainKeys = new Set();

                  // One row per unique Sales Order instead of several SOs stacked into one row.
                  const expandedPeggingData = peggingData.flatMap(explodeByLinkedSalesOrder);

                  // Tag each row with the chain "groupKey" it belongs to (rows reusing an
                  // already-shown child_element_description join that same group).
                  const groupKeyForRowIndex = new Map();
                  const seenRowSignatures = new Set();
                  const builtRowsRaw = expandedPeggingData.map((row, idx) => {
                    const key = row.child_element_description
                      ? makeChainKey(row)
                      : null;
                    const belongsToExistingGroup =
                      key && shownChainKeys.has(key);
                    const cells = buildRowCells(row, shownChainKeys);
                    const groupKey = belongsToExistingGroup
                      ? key
                      : key || row.pegging_id;
                    groupKeyForRowIndex.set(idx, groupKey);
                    return { row, cells, idx };
                  });

                  // A single Sales Order LINE ITEM's requirement is frequently split across
                  // several different supply legs by MRP (e.g. part converted to a Production
                  // Order, part still awaiting supply). Each split used to render as its own
                  // near-duplicate table row — same SO number, same item no, same total qty,
                  // but only ONE supply column filled in per row (so item 10 could show twice,
                  // once with a Production Order and once blank, while item 6 — if its only
                  // split has no supply pegged yet — looked like it had "no Production Order"
                  // even though other splits of the SAME line elsewhere might carry one).
                  // Fix: merge every split belonging to the same SO line into a SINGLE row,
                  // stacking all of its supply-side entries into the relevant category cells —
                  // exactly like Stock already stacks multiple GRN batches into one cell — so
                  // each Sales Order line item appears exactly once with everything pegged to
                  // it visible together.
                  // MERGE DISABLED: same requirement + different receipt number must show
                  // as separate lines, so we no longer collapse splits of one SO line into
                  // a single row (soDemandKey always returns null => every row stays as is).
                  const soDemandKey = (row) =>
                    false && row.parent_category === "Sales Order"
                      ? `${row.product_code}::${row.parent_element_description}::${row.parent_so_line_id ?? row.sales_order_item_no ?? ""
                      }`
                      : null;

                  const soGroups = new Map(); // demandKey -> merged { row, cells }
                  const nonSoRows = [];

                  builtRowsRaw.forEach(({ row, cells }) => {
                    const key = soDemandKey(row);
                    if (!key) {
                      nonSoRows.push({ row, cells });
                      return;
                    }
                    const existing = soGroups.get(key);
                    if (!existing) {
                      soGroups.set(key, { row, cells: { ...cells } });
                      return;
                    }
                    Object.entries(cells).forEach(([cat, entries]) => {
                      // "Sales Order" itself is already captured once by the first split —
                      // every split shares the same line/total qty, so skip repeats.
                      if (cat === "Sales Order" || !entries || !entries.length) return;
                      if (!existing.cells[cat]) existing.cells[cat] = [];
                      entries.forEach((entry) => {
                        const alreadyThere = existing.cells[cat].some(
                          (e) =>
                            e.description === entry.description &&
                            e.qty === entry.qty &&
                            e.itemNo === entry.itemNo
                        );
                        if (!alreadyThere) existing.cells[cat].push(entry);
                      });
                    });
                  });

                  const builtRowsUngrouped = [...nonSoRows, ...soGroups.values()]
                    // A row with no cells in any category column carries nothing worth showing —
                    // e.g. an SFG/RM row whose only content was the (now-hidden) requirement cell.
                    .filter(({ cells }) =>
                      Object.values(cells).some((entries) => entries && entries.length > 0)
                    )
                    // Drop exact-duplicate combinations (same PR/SO/etc. pairing reached via a
                    // different chain path) — keep only the unique requirement/fulfillment combo.
                    .filter(({ cells }) => {
                      const signature = rowCellSignature(cells);
                      if (seenRowSignatures.has(signature)) return false;
                      seenRowSignatures.add(signature);
                      return true;
                    });

                  // Stock-only rows (no requirement / other supply against them) collapse into
                  // ONE row per product: first batch + "+N" button, with a modal for all batches.
                  // Same requirement, different receipt number => separate lines.
                  // A row whose supply cell(s) (Stock / PL / PRD / PR / PO) hold several
                  // entries is split into one line per entry; the requirement side
                  // (Reorder Point / Sales Order) and single-entry supply cells repeat on
                  // every line.
                  const SUPPLY_CATS = [
                    "Stock",
                    "Planned Order",
                    "Production Order",
                    "Purchase Request",
                    "Purchase Order",
                  ];
                  const builtRowsSplit = builtRowsUngrouped.flatMap(({ row, cells }) => {
                    const lineCount = Math.max(
                      1,
                      ...SUPPLY_CATS.map((c) => (cells[c] ? cells[c].length : 0))
                    );
                    if (lineCount <= 1) return [{ row, cells }];

                    return Array.from({ length: lineCount }, (_, i) => {
                      const lineCells = { ...cells };
                      SUPPLY_CATS.forEach((c) => {
                        const list = cells[c] || [];
                        if (list.length > 1) lineCells[c] = list[i] ? [list[i]] : [];
                      });
                      return {
                        row: { ...row, pegging_id: `${row.pegging_id}-line${i}` },
                        cells: lineCells,
                      };
                    });
                  });

                  const builtRows = groupStockOnlyRows(builtRowsSplit);

                  const activeCategoryFilters = Object.entries(
                    categoryElementFilters
                  ).filter(([, v]) => v);

                  let visibleRows = builtRows;

                  if (activeCategoryFilters.length) {
                    visibleRows = visibleRows
                      .filter(({ cells }) =>
                        activeCategoryFilters.every(
                          ([cat, val]) =>
                            (cells[cat] || []).some((v) => v.description === val)
                        )
                      )
                      .map(({ row, cells, idx }) => {
                        const filteredCells = { ...cells };
                        activeCategoryFilters.forEach(([cat, val]) => {
                          filteredCells[cat] = (filteredCells[cat] || []).filter(
                            (v) => v.description === val
                          );
                        });
                        return { row, cells: filteredCells, idx };
                      });
                  }

                  if (visibleRows.length === 0) {
                    return (
                      <tr>
                        <td
                          colSpan={TOTAL_COLUMNS}
                          style={tableCell}
                        >
                          No rows match the selected Element Description filters.
                        </td>
                      </tr>
                    );
                  }

                  // Item No filter is cascaded off Sales Order: narrows each row's Sales
                  // Order cell entries down to just the selected SO line item.
                  if (soItemNoFilter) {
                    visibleRows = visibleRows
                      .filter(({ cells }) =>
                        (cells["Sales Order"] || []).some(
                          (v) => String(v.itemNo ?? "") === soItemNoFilter
                        )
                      )
                      .map(({ row, cells, idx }) => ({
                        row,
                        idx,
                        cells: {
                          ...cells,
                          "Sales Order": (cells["Sales Order"] || []).filter(
                            (v) => String(v.itemNo ?? "") === soItemNoFilter
                          ),
                        },
                      }));
                  }

                  if (visibleRows.length === 0) {
                    return (
                      <tr>
                        <td
                          colSpan={TOTAL_COLUMNS}
                          style={tableCell}
                        >
                          No rows match the selected Item No filter.
                        </td>
                      </tr>
                    );
                  }

                  return visibleRows.map(({ row, cells }) =>
                    renderRow(cells, row.pegging_id, row)
                  );
                })()
              )}
            </tbody>
          </table>
        </div>

        {/* --- Stock batches modal (opened by the "+N" button on a stock-only row) --- */}
        {stockModal && (
          <div
            onClick={() => setStockModal(null)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15,23,42,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1100,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: 12,
                width: 420,
                maxWidth: "92vw",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 20px",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#0f172a" }}>
                  Stock Batches — {stockModal.productCode}
                </h4>
                <button
                  onClick={() => setStockModal(null)}
                  title="Close"
                  style={{
                    border: "none",
                    background: "#f1f5f9",
                    borderRadius: 8,
                    width: 32,
                    height: 32,
                    cursor: "pointer",
                    fontSize: 18,
                    color: "#475569",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#d5d8dd" }}>
                      <th style={{ ...tableHeader, textAlign: "left" }}>Batch</th>
                      <th style={tableHeader}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockModal.batches.map((b, i) => (
                      <tr key={i}>
                        <td style={{ ...tableCell, textAlign: "left" }}>{b.description}</td>
                        <td style={{ ...tableCell, color: "#2e7d32", fontWeight: 700 }}>
                          +{Number(b.qty || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: "#f8fafc" }}>
                      <td style={{ ...tableCell, textAlign: "left", fontWeight: 700 }}>Total</td>
                      <td style={{ ...tableCell, fontWeight: 700 }}>
                        {stockModal.batches
                          .reduce((s, b) => s + Number(b.qty || 0), 0)
                          .toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Pegging;